// src/components/RegistrationFlow.js - FIXED with authentication state waiting
import { ArrowLeft, ArrowRight, Calendar, Check, Clock, CreditCard, MessageSquare, Phone, Scissors, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// Import MVC components
import { useAuth } from '../hooks/useAuth'; // ADDED: Import useAuth to monitor auth state
import { useRegistration } from '../hooks/useRegistration';
import { FirebaseService } from '../services/FirebaseService';

const RegistrationFlow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const urlPlanId = searchParams.get('plan');
  const urlBillingCycle = searchParams.get('billing');

  // Use registration hook for MVC integration
  const { 
    isProcessing, 
    error, 
    validationErrors, 
    validateStep, 
    getServiceEnhancements, 
    addCustomService, 
    completeRegistration 
  } = useRegistration();

  // ADDED: Monitor authentication state after registration
  const { user, pendingVerification, verificationEmail, refreshUser } = useAuth();

  const [currentStep, setCurrentStep] = useState(1);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredBusinessName, setRegisteredBusinessName] = useState('');
  const [resendStatus, setResendStatus] = useState({ state: 'idle', message: '' });
  const [checkingVerification, setCheckingVerification] = useState(false);
  
  // FIXED: Add state for step validation errors
  const [stepValidationErrors, setStepValidationErrors] = useState({});
  
  // Keep existing formData structure (no breaking changes!)
  const [formData, setFormData] = useState({
    selectedPlan: urlPlanId || 'plan2',
    billingCycle: urlBillingCycle || 'monthly',
    businessName: '',
    businessType: '',
    phoneNumber: '',
    address: '',
    numberOfChairs: '1',
    timezone: 'America/Toronto',
    services: {
      haircut: { duration: '30', price: '' },
      beard_trim: { duration: '20', price: '' },
      full_service: { duration: '60', price: '' },
      consultation: { duration: '15', price: '' }
    },
    customServices: [],
    workingHours: {
      mon: '09:00-17:00',
      tue: '09:00-17:00',
      wed: '09:00-17:00',
      thu: '09:00-17:00',
      fri: '09:00-17:00',
      sat: '09:00-17:00',
      sun: 'closed'
    },
    whatsappVerified: false,
    calendarConnected: false,
    ownerName: '',
    ownerEmail: '',
    password: '',
    confirmPassword: ''
  });

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    passwordsMatch: false
  });

  // Enhanced service management state
  const [serviceEnhancements, setServiceEnhancements] = useState({
    suggestions: [],
    categories: [],
    popularServicesByCategory: {}
  });
  const [showCustomServiceForm, setShowCustomServiceForm] = useState(false);
  const [customServiceData, setCustomServiceData] = useState({
    name: '',
    description: '',
    category: 'general',
    duration: 30,
    price: 0
  });

  // ADDED: Monitor authentication state after successful registration
  useEffect(() => {
    if (!registrationSuccess) {
      return;
    }

    if (user && user.emailVerified) {
      console.log('✅ Email verified, redirecting to dashboard');
      navigate('/dashboard', {
        state: {
          message: 'Account created successfully! Welcome to Booker.Chat!',
          isNewUser: true,
          businessName: registeredBusinessName || formData.businessName
        }
      });
    }
  }, [registrationSuccess, user, navigate, registeredBusinessName, formData.businessName]);

  useEffect(() => {
    if (!registrationSuccess || !pendingVerification) {
      return;
    }

    const interval = setInterval(() => {
      refreshUser();
    }, 5000);

    return () => clearInterval(interval);
  }, [registrationSuccess, pendingVerification, refreshUser]);

  // Business types array (maintained)
  const businessTypes = [
    { id: 'barbershop', name: 'Barbershop', icon: '✂️' },
    { id: 'hair_salon', name: 'Hair Salon', icon: '💇‍♀️' },
    { id: 'nail_salon', name: 'Nail Salon', icon: '💅' },
    { id: 'spa', name: 'Spa', icon: '🧖‍♀️' },
    { id: 'beauty_studio', name: 'Beauty Studio', icon: '💄' },
    { id: 'other', name: 'Other', icon: '🏪' }
  ];

  // Service templates (maintained)
  const serviceTemplates = {
    barbershop: {
      haircut: { duration: '30', price: '25' },
      beard_trim: { duration: '20', price: '15' },
      full_service: { duration: '60', price: '40' },
      consultation: { duration: '15', price: '0' }
    },
    hair_salon: {
      haircut: { duration: '45', price: '65' },
      color: { duration: '120', price: '150' },
      styling: { duration: '30', price: '45' },
      consultation: { duration: '15', price: '0' }
    },
    nail_salon: {
      manicure: { duration: '45', price: '35' },
      pedicure: { duration: '60', price: '45' },
      gel_polish: { duration: '30', price: '25' },
      consultation: { duration: '15', price: '0' }
    },
    spa: {
      massage: { duration: '60', price: '80' },
      facial: { duration: '75', price: '95' },
      body_treatment: { duration: '90', price: '120' },
      consultation: { duration: '15', price: '0' }
    }
  };

  // Plans object (maintained)
  const plans = {
    plan1: {
      name: 'WhatsApp Only',
      description: 'Perfect for small businesses starting with WhatsApp booking',
      features: ['WhatsApp booking automation', 'Calendar integration', 'Basic analytics', 'Email support']
    },
    plan2: {
      name: 'WhatsApp + Voice',
      description: 'Most popular - Full booking assistant with phone support',
      features: ['Everything in WhatsApp Only', 'Phone call automation', 'Advanced AI voice', 'Priority support']
    },
    plan3: {
      name: '+ Marketing & Promotions',
      description: 'Grow your business with automated marketing campaigns',
      features: ['Everything in WhatsApp + Voice', 'Marketing campaigns', 'Customer segmentation', 'Advanced insights']
    },
    plan4: {
      name: '+ Advanced Payments',
      description: 'Complete business solution with integrated payments',
      features: ['Everything in Marketing & Promotions', 'Payment processing', 'Advanced reporting', '24/7 support']
    }
  };

  // Steps array (maintained)
  const steps = [
    { id: 1, name: 'Plan Confirmation', icon: CreditCard },
    { id: 2, name: 'Business Details', icon: User },
    { id: 3, name: 'Services & Pricing', icon: Scissors },
    { id: 4, name: 'Schedule Setup', icon: Clock },
    { id: 5, name: 'Integration', icon: MessageSquare },
    { id: 6, name: 'Complete Setup', icon: Check }
  ];

  // Load service enhancements when business type changes
  useEffect(() => {
    if (formData.businessType) {
      const enhancements = getServiceEnhancements(formData);
      setServiceEnhancements(enhancements);
    }
  }, [formData.businessType, getServiceEnhancements]);

  // Helper functions (maintained)
  const getCurrentPlan = () => {
    return plans[formData.selectedPlan] || plans.plan2;
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear step validation errors when field is updated
    if (stepValidationErrors[currentStep]) {
      setStepValidationErrors(prev => ({
        ...prev,
        [currentStep]: undefined
      }));
    }

    // Password validation when password fields change
    if (field === 'password' || field === 'confirmPassword') {
      const password = field === 'password' ? value : formData.password;
      const confirmPassword = field === 'confirmPassword' ? value : formData.confirmPassword;
      
      setPasswordValidation({
        minLength: password.length >= 8,
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
        hasNumber: /\d/.test(password),
        passwordsMatch: password === confirmPassword && password.length > 0
      });
    }
  };

  // Business type change with service templates
  const handleBusinessTypeChange = (type) => {
    updateFormData('businessType', type);
    if (serviceTemplates[type]) {
      updateFormData('services', serviceTemplates[type]);
    }
  };

  // Add custom service functionality
  const handleAddCustomService = () => {
    const result = addCustomService(formData, customServiceData);
    
    if (result.success) {
      setFormData(result.updatedFormData);
      setCustomServiceData({
        name: '',
        description: '',
        category: 'general',
        duration: 30,
        price: 0
      });
      setShowCustomServiceForm(false);
      
      // Refresh enhancements
      const enhancements = getServiceEnhancements(result.updatedFormData);
      setServiceEnhancements(enhancements);
    } else {
      // Handle errors (you can show them in UI)
      console.error('Failed to add custom service:', result.errors);
    }
  };

  const handleServiceChange = (serviceKey, field, value) => {
    setFormData(prev => ({
      ...prev,
      services: {
        ...prev.services,
        [serviceKey]: {
          ...prev.services[serviceKey],
          [field]: value
        }
      }
    }));
  };

  const handleWorkingHoursChange = (day, value) => {
    setFormData(prev => ({
      ...prev,
      workingHours: {
        ...prev.workingHours,
        [day]: value
      }
    }));
  };

  // FIXED: Proper step validation and progression
  const nextStep = () => {
    // Validate current step before proceeding
    if (currentStep > 1) {
      const validation = validateStep(currentStep, formData);
      if (!validation.isValid) {
        console.log('Validation errors:', validation.errors);
        // Store validation errors and prevent progression
        setStepValidationErrors(prev => ({
          ...prev,
          [currentStep]: validation.errors
        }));
        return; // FIXED: Don't proceed if validation fails
      }
    }
    
    // Clear any existing validation errors for this step
    setStepValidationErrors(prev => ({
      ...prev,
      [currentStep]: undefined
    }));
    
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      // Clear validation errors when going back
      setStepValidationErrors(prev => ({
        ...prev,
        [currentStep]: undefined
      }));
    }
  };

  // Submit and prompt for email verification
  const handleSubmit = async () => {
    const result = await completeRegistration(formData);
    
    if (result.success) {
      console.log('✅ Registration successful, awaiting email confirmation...');
      setRegisteredEmail(formData.ownerEmail);
      setRegisteredBusinessName(formData.businessName);
      setResendStatus({ state: 'idle', message: '' });
      setCheckingVerification(false);
      setRegistrationSuccess(true);
    } else {
      const fallbackMessage = result.error || 'Please review the highlighted fields and try again.';
      const errors = Array.isArray(result.errors) && result.errors.length > 0
        ? result.errors
        : [fallbackMessage];

      console.error('Registration failed:', errors);

      setStepValidationErrors(prev => ({
        ...prev,
        6: errors
      }));
    }
  };

  const handleResendVerification = async () => {
    setResendStatus({ state: 'sending', message: '' });

    try {
      const result = await FirebaseService.sendVerificationEmail();

      if (result.success) {
        setResendStatus({ state: 'success', message: 'Verification email sent! Please check your inbox.' });
      } else {
        setResendStatus({ state: 'error', message: result.error || 'Failed to send verification email.' });
      }
    } catch (error) {
      setResendStatus({ state: 'error', message: error.message || 'Failed to send verification email.' });
    }
  };

  const handleManualVerificationCheck = async () => {
    setCheckingVerification(true);

    try {
      await refreshUser();
    } catch (error) {
      console.error('Failed to refresh verification status:', error);
    } finally {
      setCheckingVerification(false);
    }
  };

  // Check if password meets all requirements
  const isPasswordValid = () => {
    return passwordValidation.minLength &&
           passwordValidation.hasUppercase &&
           passwordValidation.hasLowercase &&
           passwordValidation.hasNumber &&
           passwordValidation.passwordsMatch &&
           formData.ownerName.trim() &&
           formData.ownerEmail.trim();
  };

  const emailVerificationTarget = verificationEmail || registeredEmail || formData.ownerEmail || 'the email address you registered with';

  // Success state rendering with email verification gating
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-4 text-center">
          <div className="mb-6">
            <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Check className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">One Last Step!</h2>
            <p className="text-gray-600">
              Your Booker.Chat account is created. Confirm your email to unlock the dashboard.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
            <p className="text-blue-900 text-sm mb-2">
              We sent a confirmation email to
            </p>
            <p className="text-blue-900 font-semibold break-words">{emailVerificationTarget}</p>
            <p className="text-blue-800 text-sm mt-3">
              Click the link in that email. This page will redirect automatically once we've confirmed it.
            </p>
          </div>

          {pendingVerification && (
            <div className="mt-4 flex items-center justify-center text-sm text-gray-600">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
              Waiting for email confirmation...
            </div>
          )}

          {resendStatus.state === 'success' && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
              {resendStatus.message}
            </div>
          )}

          {resendStatus.state === 'error' && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
              {resendStatus.message}
            </div>
          )}

          <div className="mt-6 space-y-3">
            <button
              onClick={handleManualVerificationCheck}
              disabled={checkingVerification}
              className="w-full flex items-center justify-center bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 disabled:opacity-60"
            >
              {checkingVerification ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Checking status...
                </>
              ) : (
                'I\'ve confirmed my email'
              )}
            </button>

            <button
              onClick={handleResendVerification}
              disabled={resendStatus.state === 'sending'}
              className="w-full flex items-center justify-center border border-blue-200 text-blue-700 py-2 px-4 rounded-lg hover:bg-blue-50 disabled:opacity-60"
            >
              {resendStatus.state === 'sending' ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                  Sending...
                </>
              ) : (
                'Resend verification email'
              )}
            </button>
          </div>

          <div className="mt-6 text-xs text-gray-500 space-y-1">
            <p>Tip: Check your spam or promotions folders if you don't see the email.</p>
            <p>Need to update your email? Contact support@booker.chat for help.</p>
          </div>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <p className="text-xs text-gray-500 mb-3">
              Ready to sign in after confirming?
            </p>
            <Link
              to="/login"
              state={{
                message: 'Confirm your email, then sign in to continue.',
                email: emailVerificationTarget
              }}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              Go to login →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Confirm Your Plan</h2>
              <p className="mt-2 text-gray-600">You can change this later if needed</p>
            </div>
            
            {/* Enhanced plan confirmation with more details */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className="text-lg font-medium text-gray-900">
                      {getCurrentPlan().name}
                    </h3>
                    {formData.selectedPlan === 'plan2' && (
                      <span className="ml-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full">
                        Most Popular
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{getCurrentPlan().description}</p>
                  
                  <div className="text-lg font-bold text-blue-700 mb-2">
                    Reach out for pricing — our team will follow up with a custom quote
                  </div>
                  
                  {/* Show key features */}
                  <div className="text-sm text-gray-600">
                    <p className="font-medium mb-1">Includes:</p>
                    <ul className="space-y-1">
                      {getCurrentPlan().features.slice(0, 3).map((feature, index) => (
                        <li key={index} className="flex items-center">
                          <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                          {feature.text}
                        </li>
                      ))}
                      {getCurrentPlan().features.length > 3 && (
                        <li className="text-blue-600">
                          + {getCurrentPlan().features.length - 3} more features
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
                
                <Link 
                  to="/pricing"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium whitespace-nowrap ml-4"
                >
                  Change Plan
                </Link>
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <Check className="h-5 w-5 text-green-500" />
                <span className="ml-2 text-sm text-green-700">
                  <strong>14-day free trial</strong> - No payment required to start
                </span>
              </div>
              <p className="mt-1 ml-7 text-xs text-green-600">
                Trial includes full access to all {getCurrentPlan().name} features
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Tell Us About Your Business</h2>
              <p className="mt-2 text-gray-600">This helps us customize your booking assistant</p>
            </div>
            
            {/* FIXED: Show validation errors for step 2 */}
            {stepValidationErrors[2] && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">Please fix the following issues:</h4>
                <ul className="list-disc list-inside text-sm text-red-700">
                  {stepValidationErrors[2].map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name *
                </label>
                <input
                  type="text"
                  value={formData.businessName}
                  onChange={(e) => updateFormData('businessName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Elite Hair Studio"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => updateFormData('phoneNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Type *
              </label>
              {/* ENHANCED: Show visual indication when business type is required */}
              {!formData.businessType && stepValidationErrors[2] && (
                <div className="mb-3 text-sm text-red-600">
                  ⚠️ Please select a business type to continue
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {businessTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleBusinessTypeChange(type.id)}
                    className={`p-4 border rounded-lg text-center transition-colors ${
                      formData.businessType === type.id
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-2">{type.icon}</div>
                    <div className="text-sm font-medium">{type.name}</div>
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Address *
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateFormData('address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="123 Main Street, City, State"
              />
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Chairs/Stations
                </label>
                <select
                  value={formData.numberOfChairs}
                  onChange={(e) => updateFormData('numberOfChairs', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[1,2,3,4,5,6,7,8,9,10].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => updateFormData('timezone', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="America/Toronto">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                </select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Set Up Your Services</h2>
              <p className="mt-2 text-gray-600">Configure your services and typical duration</p>
            </div>
            
            {/* Current Services */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Current Services</h3>
              {Object.entries(formData.services).map(([serviceKey, service]) => (
                <div key={serviceKey} className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid md:grid-cols-3 gap-4 items-center">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 capitalize">
                        {serviceKey.replace('_', ' ')}
                      </label>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Duration (minutes)</label>
                      <input
                        type="number"
                        value={service.duration}
                        onChange={(e) => handleServiceChange(serviceKey, 'duration', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        min="5"
                        max="300"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Price (optional)</label>
                      <input
                        type="number"
                        value={service.price}
                        onChange={(e) => handleServiceChange(serviceKey, 'price', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Service Suggestions */}
            {serviceEnhancements.suggestions && serviceEnhancements.suggestions.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Suggested Services</h3>
                <p className="text-sm text-gray-600">Popular services for {businessTypes.find(t => t.id === formData.businessType)?.name} businesses:</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {serviceEnhancements.suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        const serviceKey = suggestion.name.toLowerCase().replace(/\s+/g, '_');
                        if (!formData.services[serviceKey]) {
                          setFormData(prev => ({
                            ...prev,
                            services: {
                              ...prev.services,
                              [serviceKey]: {
                                duration: suggestion.duration,
                                price: suggestion.price
                              }
                            }
                          }));
                        }
                      }}
                      className="p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 text-left"
                    >
                      <div className="font-medium text-sm">{suggestion.name}</div>
                      <div className="text-xs text-gray-500">{suggestion.duration} min • ${suggestion.price}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Service Creation */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">Custom Services</h3>
                <button
                  onClick={() => setShowCustomServiceForm(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700"
                >
                  Add Custom Service
                </button>
              </div>

              {showCustomServiceForm && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <h4 className="font-medium text-gray-900 mb-4">Create Custom Service</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Service Name</label>
                      <input
                        type="text"
                        value={customServiceData.name}
                        onChange={(e) => setCustomServiceData({...customServiceData, name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., Hot Towel Shave"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                      <select
                        value={customServiceData.category}
                        onChange={(e) => setCustomServiceData({...customServiceData, category: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="general">General</option>
                        <option value="haircut">Haircut</option>
                        <option value="coloring">Hair Coloring</option>
                        <option value="styling">Hair Styling</option>
                        <option value="treatment">Hair Treatment</option>
                        <option value="beard">Beard Services</option>
                        <option value="nails">Nail Services</option>
                        <option value="massage">Massage Therapy</option>
                        <option value="facial">Facial Treatments</option>
                        <option value="waxing">Waxing Services</option>
                        <option value="makeup">Makeup Services</option>
                        <option value="consultation">Consultations</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes)</label>
                      <input
                        type="number"
                        value={customServiceData.duration}
                        onChange={(e) => setCustomServiceData({...customServiceData, duration: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        min="5"
                        max="480"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Price ($)</label>
                      <input
                        type="number"
                        value={customServiceData.price}
                        onChange={(e) => setCustomServiceData({...customServiceData, price: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        min="0"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                    <textarea
                      value={customServiceData.description}
                      onChange={(e) => setCustomServiceData({...customServiceData, description: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows="2"
                      placeholder="Brief description of the service..."
                    />
                  </div>
                  <div className="mt-4 flex space-x-3">
                    <button
                      onClick={handleAddCustomService}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
                    >
                      Add Service
                    </button>
                    <button
                      onClick={() => setShowCustomServiceForm(false)}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Clock className="h-5 w-5 text-blue-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Why do we need service durations?
                  </h3>
                  <p className="mt-1 text-sm text-blue-700">
                    This helps our AI prevent double-bookings by knowing how long each service takes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Set Your Working Hours</h2>
              <p className="mt-2 text-gray-600">When should the AI assistant accept bookings?</p>
            </div>
            
            <div className="space-y-4">
              {Object.entries(formData.workingHours).map(([day, hours]) => (
                <div key={day} className="flex items-center space-x-4">
                  <div className="w-20">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {day}
                    </label>
                  </div>
                  <div className="flex-1">
                    {hours === 'closed' ? (
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500 text-sm">Closed</span>
                        <button
                          onClick={() => handleWorkingHoursChange(day, '09:00-17:00')}
                          className="text-blue-600 hover:text-blue-700 text-sm"
                        >
                          Open this day
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <input
                          type="time"
                          value={hours.split('-')[0]}
                          onChange={(e) => {
                            const endTime = hours.split('-')[1];
                            handleWorkingHoursChange(day, `${e.target.value}-${endTime}`);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <span>to</span>
                        <input
                          type="time"
                          value={hours.split('-')[1]}
                          onChange={(e) => {
                            const startTime = hours.split('-')[0];
                            handleWorkingHoursChange(day, `${startTime}-${e.target.value}`);
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <button
                          onClick={() => handleWorkingHoursChange(day, 'closed')}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Close
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Connect Your Tools</h2>
              <p className="mt-2 text-gray-600">Link WhatsApp and Calendar for seamless booking</p>
            </div>
            
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <MessageSquare className="h-8 w-8 text-green-500" />
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">WhatsApp Business</h3>
                      <p className="text-sm text-gray-600">Connect your business WhatsApp number</p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateFormData('whatsappVerified', true)}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      formData.whatsappVerified
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {formData.whatsappVerified ? 'Connected' : 'Connect'}
                  </button>
                </div>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Calendar className="h-8 w-8 text-blue-500" />
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">Google Calendar</h3>
                      <p className="text-sm text-gray-600">Sync appointments with your calendar</p>
                    </div>
                  </div>
                  <button
                    onClick={() => updateFormData('calendarConnected', true)}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      formData.calendarConnected
                        ? 'bg-green-100 text-green-800'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {formData.calendarConnected ? 'Connected' : 'Connect'}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Phone className="h-5 w-5 text-yellow-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Voice calls setup
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    Phone integration will be automatically configured based on your phone number.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900">Create Your Account</h2>
              <p className="mt-2 text-gray-600">Set up your login credentials and complete your profile</p>
            </div>

            {stepValidationErrors[6] && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-medium text-red-900 mb-2">We need a bit more information:</h4>
                <ul className="list-disc list-inside text-sm text-red-700">
                  {stepValidationErrors[6].map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={formData.ownerName}
                  onChange={(e) => updateFormData('ownerName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John Smith"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.ownerEmail}
                  onChange={(e) => updateFormData('ownerEmail', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="john@example.com"
                />
              </div>
            </div>

            {/* NEW: Password Section */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password *
                </label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => updateFormData('password', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Create a secure password"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password *
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => updateFormData('confirmPassword', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Confirm your password"
                />
              </div>

              {/* Password Requirements */}
              {formData.password && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Password Requirements:</h4>
                  <div className="grid md:grid-cols-2 gap-2 text-sm">
                    <div className={`flex items-center ${passwordValidation.minLength ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center text-xs ${passwordValidation.minLength ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>
                        {passwordValidation.minLength ? '✓' : ''}
                      </span>
                      At least 8 characters
                    </div>
                    <div className={`flex items-center ${passwordValidation.hasUppercase ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center text-xs ${passwordValidation.hasUppercase ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>
                        {passwordValidation.hasUppercase ? '✓' : ''}
                      </span>
                      One uppercase letter
                    </div>
                    <div className={`flex items-center ${passwordValidation.hasLowercase ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center text-xs ${passwordValidation.hasLowercase ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>
                        {passwordValidation.hasLowercase ? '✓' : ''}
                      </span>
                      One lowercase letter
                    </div>
                    <div className={`flex items-center ${passwordValidation.hasNumber ? 'text-green-600' : 'text-gray-500'}`}>
                      <span className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center text-xs ${passwordValidation.hasNumber ? 'bg-green-500 text-white' : 'bg-gray-300'}`}>
                        {passwordValidation.hasNumber ? '✓' : ''}
                      </span>
                      One number
                    </div>
                  </div>
                  
                  {formData.confirmPassword && (
                    <div className={`flex items-center mt-2 ${passwordValidation.passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                      <span className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center text-xs ${passwordValidation.passwordsMatch ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                        {passwordValidation.passwordsMatch ? '✓' : '✗'}
                      </span>
                      Passwords match
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="text-lg font-medium text-green-800 mb-4">Setup Summary</h3>
              <div className="space-y-2 text-sm">
                <div>✅ Plan: {getCurrentPlan().name}</div>
                <div>✅ Business: {formData.businessName}</div>
                <div>✅ Type: {businessTypes.find(t => t.id === formData.businessType)?.name}</div>
                <div>✅ Services: {Object.keys(formData.services).length} configured</div>
                <div>✅ WhatsApp: {formData.whatsappVerified ? 'Connected' : 'Pending'}</div>
                <div>✅ Calendar: {formData.calendarConnected ? 'Connected' : 'Pending'}</div>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Clock className="h-5 w-5 text-blue-500" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Your 14-day free trial starts now!
                  </h3>
                  <p className="mt-1 text-sm text-blue-700">
                    We'll send setup instructions to your email and help you test your first booking.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* UPDATED: Navigation bar with login link */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-blue-600" />
              <span className="ml-2 text-xl font-bold text-gray-900">Booker.Chat</span>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Already have an account?
              </span>
              <Link 
                to="/login" 
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Sign In
              </Link>
              <span className="text-gray-300">|</span>
              <Link 
                to="/support" 
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Need Help?
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Progress bar (maintained) */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  currentStep > step.id
                    ? 'bg-green-500 border-green-500 text-white'
                    : currentStep === step.id
                    ? 'border-blue-500 text-blue-500'
                    : 'border-gray-300 text-gray-300'
                }`}>
                  {currentStep > step.id ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <step.icon className="w-4 h-4" />
                  )}
                </div>
                <span className={`ml-2 text-sm font-medium ${
                  currentStep >= step.id ? 'text-gray-900' : 'text-gray-500'
                }`}>
                  {step.name}
                </span>
                {index < steps.length - 1 && (
                  <div className={`mx-4 h-0.5 w-8 ${
                    currentStep > step.id ? 'bg-green-500' : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow p-8">
          {/* Show processing state */}
          {isProcessing && (
            <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-3"></div>
                Creating your account...
              </div>
            </div>
          )}

          {/* Show errors */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Show validation errors */}
          {validationErrors[currentStep] && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
              <ul className="list-disc list-inside">
                {validationErrors[currentStep].map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Password validation error on final step */}
          {currentStep === 6 && formData.password && !isPasswordValid() && (
            <div className="mb-4 bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
              <p className="text-sm">
                Please complete all required fields and ensure your password meets all requirements to continue.
              </p>
            </div>
          )}

          {renderStepContent()}
          
          {/* Navigation buttons */}
          <div className="mt-8 flex justify-between">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg ${
                currentStep === 1
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </button>
            
            {currentStep === 6 ? (
              <button
                onClick={handleSubmit}
                disabled={isProcessing || !isPasswordValid()}
                className="flex items-center px-6 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Account...
                  </>
                ) : (
                  <>
                    Complete Setup
                    <Check className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            ) : (
              <button
                onClick={nextStep}
                className="flex items-center px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
              >
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrationFlow;
